import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Crown, Quote } from "lucide-react";
import { T } from "@/locales/tr";

const PORTRAIT = `${process.env.PUBLIC_URL || ""}/papicoo-portrait.png`;
const LANDSCAPE = `${process.env.PUBLIC_URL || ""}/papicoo-retouche-hero.png`;

const pillars = [
  { icon: Crown, title: "Force tranquille", text: T("Verbe choisi, regard juste, jamais le tumulte. On élève le débat, on n'écrase personne.") },
  { icon: ShieldCheck, title: "Sécurité absolue", text: T("Aucun mot de passe demandé. Aucune intrusion dans la vie privée. OAuth officiel uniquement.") },
  { icon: Sparkles, title: "L'islam comme boussole", text: T("Chaque réponse adossée au Coran (Sourate + verset). Diplomate. Corrige avec preuves.") },
];

export default function About() {
  return (
    <div data-testid="page-about" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Manifeste")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.95]">
            {T("Le conseiller")}<br />
            <span className="italic font-light">{T("que la jeunesse")}</span><br />
            {T("sénégalaise n'a jamais eu.")}
          </h1>

          <p className="mt-6 text-base sm:text-lg text-[color:var(--papicoo-muted)] leading-relaxed max-w-xl">
            {T("Papicoo n'est pas un personnage. C'est une posture : celle de l'homme qui marche droit, qui parle juste, qui élève sans humilier. Il ne suit que ce que les prophètes ont suivi — la voie du")} <em>{T("tawhîd")}</em>{T(", de la justice, de la miséricorde et du travail bien fait.")}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {["Islam authentique", "Sénégal 🇸🇳", "France 🇫🇷", "Intelligence", "Culture", "Ouverture", "Vertu"].map((t) => (
              <span key={t} className="px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] text-xs text-[color:var(--papicoo-ink)]">
                {t}
              </span>
            ))}
          </div>

          <div className="mt-6 text-sm text-[color:var(--papicoo-muted)] leading-relaxed max-w-xl">
            {T("Photographe · Directeur Artistique · Web Designer · Maquettiste — basé à")} <strong>{T("Paris")}</strong>{T(". La création visuelle au service de la vérité, et la vérité au service des hommes.")}
          </div>

          <div className="mt-8 papicoo-card p-6">
            <Quote size={20} className="text-[color:var(--papicoo-gold)]" />
            <p className="font-display italic text-2xl text-[color:var(--papicoo-ink)] mt-3 leading-snug">
              {T("« Ô David ! Nous avons fait de toi un calife sur la terre. Juge donc entre les gens en toute équité et ne suis pas la passion. »")}
            </p>
            <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)] mt-3">{T("Sourate Sâd · 38 : 26")}</div>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {pillars.map((p, i) => (
              <div key={p.title} data-testid={`pillar-${i}`} className="papicoo-card p-5">
                <p.icon size={22} className="text-[color:var(--papicoo-gold)]" strokeWidth={1.5} />
                <div className="font-display text-xl text-[color:var(--papicoo-ink)] mt-3">{T(p.title)}</div>
                <p className="text-sm text-[color:var(--papicoo-muted)] mt-1 leading-relaxed">{T(p.text)}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/chat" data-testid="about-cta-chat" className="inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary">
              {T("Échanger avec Papicoo")} <ArrowRight size={18} />
            </Link>
            <Link to="/coran" data-testid="about-cta-coran" className="inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-gold">
              {T("Explorer les versets")}
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="papicoo-card papicoo-grain overflow-hidden">
            <img src={PORTRAIT} alt={T("Papicoo portrait")} className="w-full aspect-[4/5] object-cover" />
            <div className="p-5">
              <div className="signature-script text-xl text-[color:var(--papicoo-ink)]">{T("Papicoo")}</div>
              <div className="text-xs text-[color:var(--papicoo-muted)] mt-1"></div>
            </div>
          </div>
          <div className="papicoo-card overflow-hidden">
            <img src={LANDSCAPE} alt={T("Œuvre Papicoo")} className="w-full h-56 object-cover" />
            <div className="p-5">
              <div className="font-display text-xl text-[color:var(--papicoo-ink)]">{T("L'œil et le verbe.")}</div>
              <p className="text-sm text-[color:var(--papicoo-muted)] mt-1 leading-relaxed">
                {T("Une image de Papicoo, parmi d'autres — extraite de papicoo.fr. Direction artistique, photographie, lumière juste.")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
